import { TherapistSearchResponseDto } from '@/app/api';
import React, { forwardRef, useEffect, useRef, useState } from 'react'
import { COLORS } from '@app/constants';
import styled from 'styled-components';
import { Play } from '@app/ui/iconComponents';
import { BreakpointsEnum, TherapistData } from '@app/types';
import { Bag } from '../../iconComponents';
import { makeFriendlyAge } from '@/app/utils/makeFriendlyAge';
import ButtonLink from '../ButtonLink';
import Button from '../Button';
import { DateUtil } from '@/app/utils/dateUtility';
import BasicModal from '@/landing/ui/components/BasicModal';
import { useClientSize } from '@/app/hooks';
import { useRouter } from 'next/router';
import { getSpecializations } from '@/app/utils/getSpecializations';
import { useOnClickOutside } from '@/b2b/shared/hooks';

interface Props {
  data: TherapistSearchResponseDto;
  child?: boolean;
  handleClickSignupButton: (therapist: TherapistData) => void;
  toggleEnrollModalOpen: (therapist: TherapistData) => void;
  hidePrice?: boolean;
  showBookSessionButton?: boolean;
}

const SpecialistCardNew = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    data,
    child,
    handleClickSignupButton,
    toggleEnrollModalOpen,
    hidePrice = false,
    showBookSessionButton = true,
  } = props;

  const router = useRouter();
  const { getIsBreakpoint } = useClientSize();
  const isMobile = getIsBreakpoint('sm');
  const { startDate } = data.closestFreeAvailability;
  const isToday = DateUtil.now(startDate).isToday();
  const isTomorrow = DateUtil.now(startDate).isTomorrow();
  const formattedStartDate = isToday
    ? `Сегодня, ${DateUtil.now(startDate).format('HH:00')}`
    : isTomorrow
      ? `Завтра, ${DateUtil.now(startDate).format('HH:00')}`
      : DateUtil.now(startDate).format('DD MMMM, HH:00');

  const workingExperience = `${makeFriendlyAge(data.workingExperience)} практики`;

  function formatIntegerWithSpaces(number: number) {
    const numStr = number.toString();
    let integerPartWithSpaces = '';
    let counter = 0;

    for (let i = numStr.length - 1; i >= 0; i--) {
      integerPartWithSpaces = numStr[i] + integerPartWithSpaces;
      counter++;
      if (counter % 3 === 0 && i > 0) {
        integerPartWithSpaces = ' ' + integerPartWithSpaces;
      }
    }
    return integerPartWithSpaces;
  }

  const BookSessionButton = (
    <BookButton
      styleType="primary"
      onClick={() => {
        data?.closestFreeAvailability?.startDate
          ? handleClickSignupButton(data as TherapistData)
          : toggleEnrollModalOpen(data as TherapistData);
      }}
    >
      <span>Записаться</span>
    </BookButton>
  );

  const [videoIsOpen, setVideoIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<JSX.Element>();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setModalContent(
      <ModalVideo
        ref={videoRef}
        controls
        src={data.videoKey}
        onEnded={() => setVideoIsOpen(false)}
      />
    );

    if (videoIsOpen && videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.play().catch((error) => {
        console.error('Ошибка при попытке воспроизвести видео', error);
      });
    }

    if (!videoIsOpen && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      videoRef.current.muted = true;
    }
  }, [videoIsOpen, data.videoKey]);

  useOnClickOutside(videoRef, () => setVideoIsOpen(false));

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && videoIsOpen) {
        setVideoIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [videoIsOpen]);

  return (
    <Root ref={ref}>
      <MainBlock>
        {data.avatarKey && (
          <Avatar $backgroundImage={data.avatarKey} />
        )}
        {showBookSessionButton && !isMobile &&
          <>
            {BookSessionButton}
            <AvailableTimeWrap>
              <CardSubtitle>Ближайшее время: </CardSubtitle>
              <AvailableTime
                onClick={() => {
                  data?.closestFreeAvailability?.startDate
                    ? handleClickSignupButton(data as TherapistData)
                    : toggleEnrollModalOpen(data as TherapistData);
                }}>
                {data?.closestFreeAvailability?.startDate
                  ? formattedStartDate
                  : 'По запросу'}
              </AvailableTime>
            </AvailableTimeWrap>
          </>
        }
      </MainBlock>

      <AboutBlock>
        <PersonWrap>
          <UserName href={`/therapist/${data.url}${child ? '/?tab=child' : ''}`}>
            <div>{data.firstname}</div>
            <div>{data.lastname}</div>
          </UserName>
          {isMobile && data.price && (
            <InfoLabel>
              {!hidePrice && <>{formatIntegerWithSpaces(data.price)} ₽</>}
              <span>Индивидуальная консультация  ~50 мин.</span>
            </InfoLabel>
          )}
        </PersonWrap>

        <AchievementsWrap>
          {data?.specialisation?.length && data.specialisation.map((el, key) => (
            <RecommendTag key={key}><span>{getSpecializations(el)}</span></RecommendTag>
          ))}
          {data.workingExperience && (
            <AchievementTag>
              <Bag color={COLORS.primary300} />
              <span>{workingExperience}</span>
            </AchievementTag>
          )}
        </AchievementsWrap>

        {data.videoKey && (
          <VideoLinkWrap>
            <VideoLink
              onClick={() => {
                setVideoIsOpen(true);
              }}
            >
              <Play color={COLORS.primary300} width={17} height={17} />
              <span>Посмотреть видео-визитку</span>
            </VideoLink>
          </VideoLinkWrap>
        )}

        {!isMobile && data.price && (
          <InfoLabel>
            {!hidePrice && <>{formatIntegerWithSpaces(data.price)} ₽</>}
            <span>Индивидуальная консультация ~50 мин.</span>
          </InfoLabel>
        )}

        {(data?.shortAbout?.length != 0 || data?.about?.length != 0) &&
          <Description>{data?.shortAbout || data?.about}</Description>
        }

        <ActionsBlock>
          <LinkButton
            href={undefined}
            onClick={() => {
              router.push({
                pathname: '/therapist/[url]',
                query: {
                  url: data.url,
                  ...(child && { tab: 'child' })
                },
              })
            }}
          >
            <span>Подробнее</span>
          </LinkButton>

          {showBookSessionButton && isMobile &&
            <>
              {BookSessionButton}
              <AvailableTimeWrap>
                <CardSubtitle>Ближайшее время: </CardSubtitle>
                <AvailableTime
                  onClick={() => {
                    data?.closestFreeAvailability?.startDate
                      ? handleClickSignupButton(data as TherapistData)
                      : toggleEnrollModalOpen(data as TherapistData);
                  }}
                >
                  {data?.closestFreeAvailability?.startDate
                    ? formattedStartDate
                    : 'По запросу'
                  }
                </AvailableTime>
              </AvailableTimeWrap>
            </>
          }
        </ActionsBlock>
      </AboutBlock>

      <BasicModal
        backgroundColor="transparent"
        visible={videoIsOpen}
        setVisible={setVideoIsOpen}
        padding='40px'
        mobilePadding='0'
        mobileHeight='100%'
        mobileWidth='100%'
        positionMobileSvgTop='-2px'
        overflow='hidden'
        closeButtonColor={COLORS.white}
      >
        {modalContent}
      </BasicModal>
    </Root>
  )
});

SpecialistCardNew.displayName = 'SpecialistCardNew';

export default SpecialistCardNew;

interface StyleProps {
  $isMobile?: boolean;
  $backgroundImage?: string
}

const Root = styled.div`
  position: relative;
  display: flex;
  gap: 32px;
  width: 960px;
  border-radius: 24px;
  padding: 32px;
  background: ${COLORS.white};

  @media (max-width: ${BreakpointsEnum.lg}px) {
    width: 69.7vw;
  }

  @media (max-width: ${BreakpointsEnum.md}px) {
    width: 56vw;
    gap: 16px;
  }

  @media (max-width: ${BreakpointsEnum.ms}px) {
    width: 55vw;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    flex-direction: column;
    gap: 16px;
    width: 92.2vw;
    height: 100%;
    padding: 16px;
    border-radius: 12px;
  }
`;

const MainBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 250px;

  @media (max-width: ${BreakpointsEnum.sm}px) { width: 100%; }
`;

const Avatar = styled.div<StyleProps>`
  width: 250px;
  height: 250px;
  border-radius: 8px;
  margin-bottom: 8px;
  background: url(${props => props.$backgroundImage});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;

  @media (max-width: ${BreakpointsEnum.md}px) {
    width: 150px;
    height: 150px;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    width: 100%;
    height: 300px;
    margin: 0 auto;
    border-radius: 8px;
    background-size: cover;
  }
`;

const BookButton = styled(Button)`
  width: 250px;
  padding: 16px;
  font-size: 16px;
  font-weight: 600;
  line-height: 21px;
  cursor: pointer;
  background-color: ${COLORS.secondary300};
  color: ${COLORS.black};

  &:hover {
    background-color: ${COLORS.secondary400} !important;
  }

  @media (max-width: ${BreakpointsEnum.md}px) {
    width: 150px;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    grid-column: 2;
    grid-row: 1;
    width: 100%;
  }
`;

const AvailableTimeWrap = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  text-align: center;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    grid-column: 1 / span 2;
    grid-row: 2;
  }
`;

const CardSubtitle = styled.p`
  display: inline;
  color: #8F97A0;
`;

const AvailableTime = styled.p`
  display: inline;
  color: ${COLORS.primary300};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const AboutBlock = styled.div`
  width: 693px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: ${BreakpointsEnum.md}px) {
    gap: 12px;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    gap: 16px;
    width: 100%;
  }
`;

const PersonWrap = styled.div`
  @media (max-width: ${BreakpointsEnum.sm}px) {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const UserName = styled.a`
  color: ${COLORS.black};
  font-size: 32px;
  font-weight: 600;
  line-height: 32px;
  cursor: pointer;

  div:not(:last-child) {
    margin-bottom: 8px;
  }

  &:hover {
    color: ${COLORS.primary300};
  }

  @media (max-width: ${BreakpointsEnum.md}px) {
    font-size: 20px;
    line-height: 20px;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    div:not(:last-child) {
      margin-bottom: 8px;
    }
  }
`;

const AchievementsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 16px;
  column-gap: 8px;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    gap: 8px;
  }
`;

const AchievementTag = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 16px;
  background-color: ${COLORS.grayscale100};
  min-height: 32px;

  span {
    color: ${COLORS.gray1};
    font-size: 14px;
    font-weight: 500;
    line-height: 14px;
  }

  svg {
    height: 20px;
  }

  @media (max-width: ${BreakpointsEnum.md}px) {
    span {
      font-size: 14px;
      line-height: 14px;
    }
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    gap: 8px;
    padding: 4px 8px;
    border-radius: 16px;

    svg {
      height: 20px;
    }
  }
`;

const RecommendTag = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 11px;
  background-color: ${COLORS.primary300};
  border-radius: 16px;
  min-height: 32px;

  span {
    font-size: 14px;
    font-weight: 500;
    line-height: 14px;
    color: ${COLORS.white};
  }

  @media (max-width: ${BreakpointsEnum.md}px) {
    padding: 5px 8px;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    padding: 8px 10px;
    border-radius: 16px;
  }
`;

const InfoLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: ${COLORS.grayscale700};
  white-space: nowrap;

  span {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${COLORS.DarkGray};
  }

  @media (max-width: ${BreakpointsEnum.lgm}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  @media (max-width: ${BreakpointsEnum.md}px) {
    font-size: 20px;
    line-height: 20px;

    span{
      font-size: 11px;
    }
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    span {
      display: none;
    }
  }
`;

const Description = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  color: ${COLORS.grayscale600};
`;

const ActionsBlock = styled.div`
  display: flex;
  gap: 34px;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto;
    justify-content: center;
    gap: 8px;
  }
`;

const LinkButton = styled(ButtonLink)`
  width: 238px;
  padding: 16px;
  border: 1px solid ${COLORS.primary300};
  font-size: 16px;
  font-weight: 600;
  line-height: 21px;
  color: ${COLORS.primary300};
  background-color: transparent;
  cursor: pointer;

  &:hover {
    color: ${COLORS.white};
  }

  @media (max-width: ${BreakpointsEnum.md}px) {
    width: 100%;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    grid-column: 1;
    grid-row: 1;
  }
`;

const VideoLinkWrap = styled.div`
  display: flex;
`;

const VideoLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: 44px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${COLORS.primary300};
  cursor: pointer;
  border: 1px solid ${COLORS.primary300};
  border-radius: 30px;
  padding: 12px 16px;

  svg{
    margin-left: -2px;
  }

  span {
    margin-left: 10px;
    line-height: 15px;
  }

  &:hover {
    background-color: ${COLORS.primary100};
    color: ${COLORS.white};

    path {
      fill: ${COLORS.white};
      stroke: ${COLORS.white};
    }
  }

  &:active {
    background-color: ${COLORS.primary200};
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    width: 100%;

    svg{
      margin-left: -3px;
    }
  }
`;

const ModalVideo = styled.video`
  border-radius: 30px;
  max-width: 600px;
  max-height: 75vh;
  height: auto;
  width: auto;
  overflow: hidden;
  object-fit: contain;

  @media screen and ( max-width: ${BreakpointsEnum.md}px) {
    max-width: calc(100vw - 40px);
    max-height: 100vh;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  @media screen and ( max-width: ${BreakpointsEnum.sm}px) {
    border-radius: 20px;
  }
`;
